/**
 * Dialog: choose the tracker (Voluum, Binom, and Tres Commas where enabled) and
 * set the external campaign for selected wiserIds.
 * For Media Devoted | Created By Ciro Autiero
 */

import { type FC, useEffect, useState } from 'react';
import { X } from 'lucide-react';

import { Button, Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui';
import { useEnabledTrackers } from '@/features/trackers/useEnabledTrackers';
import type { PatchTrafficProvider } from '@/hooks';
import { getApiErrorMessage } from '@/lib/api-error';

import { canSaveLink, isTresCommasLink, linkChoices } from './link-dialog/link-choices';
import { TrafficCampaignField } from './link-dialog/TrafficCampaignField';
import { TrafficProviderTiles } from './link-dialog/TrafficProviderTiles';
import { useTrafficLinkSave } from './link-dialog/useTrafficLinkSave';
import { backdropClick } from '@/components/ui';

export interface TrafficSourceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  countryKey: string;
  wiserIds: number[];
  initialProvider: PatchTrafficProvider;
  initialTrafficCampaignId: string | null;
  onSaved: () => void;
}

export const TrafficSourceDialog: FC<TrafficSourceDialogProps> = ({
  open,
  onOpenChange,
  countryKey,
  wiserIds,
  initialProvider,
  initialTrafficCampaignId,
  onSaved,
}) => {
  const patch = useTrafficLinkSave(countryKey, initialProvider);
  const choices = linkChoices(useEnabledTrackers());
  const [provider, setProvider] = useState<PatchTrafficProvider>(initialProvider);
  const [campaignId, setCampaignId] = useState(initialTrafficCampaignId ?? '');

  useEffect(() => {
    if (!open) return;
    setProvider(initialProvider);
    setCampaignId(initialTrafficCampaignId ?? '');
  }, [open, initialProvider, initialTrafficCampaignId]);

  if (!open) return null;

  const close = () => {
    if (patch.isPending) return;
    onOpenChange(false);
  };

  const save = async () => {
    const trimmed = campaignId.trim();
    await patch.save({
      wiserIds,
      trafficProvider: provider,
      trafficCampaignId: trimmed === '' ? null : trimmed,
    });
    onSaved();
    onOpenChange(false);
  };

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 p-4"
      role="presentation"
      onClick={backdropClick(close)}
    >
      <Card
        className="relative w-full max-w-md shadow-lg"
        role="dialog"
        aria-modal="true"
        aria-labelledby="traffic-source-title"
        onClick={(e) => e.stopPropagation()}
      >
        <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2">
          <CardTitle id="traffic-source-title" className="text-lg">
            Traffic source
          </CardTitle>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8 shrink-0"
            onClick={close}
            disabled={patch.isPending}
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Link {wiserIds.length} campaign{wiserIds.length === 1 ? '' : 's'} to a tracker campaign id.
          </p>
          <TrafficProviderTiles
            choices={choices}
            value={provider}
            onChange={setProvider}
            disabled={patch.isPending}
          />
          <TrafficCampaignField
            provider={provider}
            value={campaignId}
            onChange={setCampaignId}
            disabled={patch.isPending}
          />
          {patch.error && isTresCommasLink(provider) ? (
            <p className="text-xs text-destructive">
              {getApiErrorMessage(patch.error, 'Could not save the Tres Commas link.')}
            </p>
          ) : null}
        </CardContent>
        <CardFooter className="flex justify-end gap-2 border-t pt-4">
          <Button type="button" variant="outline" onClick={close} disabled={patch.isPending}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => void save()}
            disabled={patch.isPending || !canSaveLink(provider, campaignId)}
          >
            {patch.isPending ? 'Saving…' : 'Save'}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
};
